import * as React from "react"
import { createPortal } from "react-dom"
import styled from "styled-components"
import { desktop, mobile, space, tablet } from "./Layout"
import Cross2 from "url:assets/images/icons/Cross2.svg"

interface Props {
  ariaLabel: string
  children: React.ReactNode
  onClose: () => void
  variant?: "dark" | "light"
  darkerOverlay?: boolean
  slideUpOnMobile?: boolean
}

const Background = styled.div<{ darkerOverlay?: boolean }>((props) => ({
  alignItems: "center",
  backgroundColor: props.darkerOverlay ? "#000000CC" : "#00000033",
  display: "flex",
  height: "100%",
  justifyContent: "center",
  position: "fixed",
  top: 0,
  transform: "translateZ(0)",
  width: "100%",
  zIndex: 1002,
}))

const Box = styled.div<{ variant?: "dark" | "light"; slideUpOnMobile?: boolean }>((props) => ({
  backgroundColor: props.variant === "light" ? "#ffffff" : "#000928",
  color: props.variant === "light" ? "#000000" : "#ffffff",
  overflowY: "auto",
  overscrollBehavior: "none",
  padding: 2 * space,
  position: "relative",
  [mobile]: {
    borderRadius: props.slideUpOnMobile ? "16px 16px 0 0" : 10,
    maxHeight: "90%",
    width: props.slideUpOnMobile ? "100%" : "90%",
    position: props.slideUpOnMobile ? "fixed" : "relative",
    bottom: props.slideUpOnMobile ? 0 : "auto",
    animation: props.slideUpOnMobile ? "slideUp 0.3s ease-out" : "none",
    "@keyframes slideUp": {
      from: {
        transform: "translateY(100%)",
      },
      to: {
        transform: "translateY(0)",
      },
    },
  },
  [tablet]: {
    borderRadius: props.theme.borderRadius || 10,
    maxHeight: "75%",
    maxWidth: "85%",
    position: "relative",
    bottom: "auto",
  },
  [desktop]: {
    borderRadius: props.theme.borderRadius || 10,
    maxHeight: "95%",
    maxWidth: 1000,
    minWidth: 600,
    position: "relative",
    bottom: "auto",
  },
}))

const CloseButton = styled.button<{ variant?: "dark" | "light" }>((props) => ({
  background: "transparent",
  border: "1px solid transparent",
  cursor: "pointer",
  lineHeight: 1,
  padding: space,
  position: "absolute",
  right: 0,
  top: 0,
  filter: props.variant === "light" ? "invert(1)" : "none",
  "&:focus": {
    border: props.variant === "light" ? "1px solid black" : "1px solid white",
  },
}))

export const ModalOverlay: React.FC<Props> = ({
  ariaLabel,
  children,
  onClose,
  variant = "dark",
  darkerOverlay = false,
  slideUpOnMobile = false,
}) => {
  const mouseDownTargetRef = React.useRef<Node | null>(null)
  const mouseUpTargetRef = React.useRef<Node | null>(null)
  const backgroundRef = React.useRef<HTMLDivElement | null>(null)
  const closeButton = React.useRef<HTMLButtonElement | null>(null)

  React.useEffect(() => {
    closeButton.current.focus()
  }, [closeButton])

  const onBackgroundMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    if (event.target instanceof Node) {
      mouseDownTargetRef.current = event.target
    }
  }
  const onBackgroundMouseUp = (event: React.MouseEvent<HTMLElement>) => {
    if (event.target instanceof Node) {
      mouseUpTargetRef.current = event.target
    }
  }
  const onBackgroundClick = () => {
    if (
      mouseDownTargetRef.current === backgroundRef.current &&
      mouseUpTargetRef.current === backgroundRef.current
    )
      onClose()
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    if (event.key.startsWith("Esc")) onClose()
    event.stopPropagation()
  }

  return createPortal(
    <Background
      aria-label={ariaLabel}
      aria-modal="true"
      onMouseDown={onBackgroundMouseDown}
      onMouseUp={onBackgroundMouseUp}
      onClick={onBackgroundClick}
      onKeyDown={onKeyDown}
      ref={backgroundRef}
      role="dialog"
      tabIndex={-1}
      darkerOverlay={darkerOverlay}
    >
      <Box variant={variant} slideUpOnMobile={slideUpOnMobile}>
        <CloseButton aria-label="Close Modal" onClick={onClose} ref={closeButton} variant={variant}>
          <img alt="Close Modal" src={Cross2} />
        </CloseButton>
        {children}
      </Box>
    </Background>,
    document.body
  )
}

interface ModalProps {
  children: React.ReactNode
  description: string
  triggerElement: React.ReactElement
  variant?: "dark" | "light"
}

export const Modal = ({ children, description, triggerElement, variant = "dark" }: ModalProps) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const trigger = React.cloneElement(triggerElement, {
    onClick: (event: MouseEvent) => {
      event.preventDefault()
      document.querySelector("html").style.overflow = "hidden"
      setIsOpen(true)
    },
  })
  const onClose = () => {
    document.querySelector("html").style.overflow = "auto"
    setIsOpen(false)
  }
  return (
    <>
      {trigger}
      {isOpen && (
        <ModalOverlay ariaLabel={description} onClose={onClose} variant={variant}>
          {children}
        </ModalOverlay>
      )}
    </>
  )
}
